import { useEffect, useState } from "react";
import { BarChart3, Coins, ArrowUpRight, ArrowDownRight, Calendar } from "lucide-react";
import { Card, CardBody, CardHeader } from "../../components/Card";
import { Button } from "../../components/Button";
import { api } from "../../lib/api";

interface UsageResponse {
  range: { from: string; to: string };
  total: {
    inputTokens: number;
    outputTokens: number;
    eventCount: number;
    cost: number;
  };
  byTenant: Array<{
    tenantId: string;
    displayName: string;
    inputTokens: number;
    outputTokens: number;
    eventCount: number;
    cost: number;
  }>;
  byModel: Array<{
    modelId: string;
    displayName: string;
    inputTokens: number;
    outputTokens: number;
    eventCount: number;
    cost: number | null;
  }>;
  byDay: Array<{
    day: string;
    inputTokens: number;
    outputTokens: number;
    eventCount: number;
  }>;
}

type Preset = "7d" | "30d" | "custom";

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function AdminUsage() {
  const [preset, setPreset] = useState<Preset>("7d");
  const [from, setFrom] = useState(dateNDaysAgo(7));
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setPresetRange = (p: Preset) => {
    setPreset(p);
    if (p === "7d") {
      setFrom(dateNDaysAgo(7));
      setTo(todayISO());
    } else if (p === "30d") {
      setFrom(dateNDaysAgo(30));
      setTo(todayISO());
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ from, to });
      const r = await api.get<UsageResponse>(`/api/admin/usage?${params}`);
      setData(r);
    } catch (err) {
      setError((err as Error).message ?? "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Bedrock usage & cost</h1>
        <p className="mt-1 text-sm text-gray-500">
          Aggregated token consumption across all tenants. Costs are estimates based on AWS Bedrock
          published pricing — actual AWS bill may differ slightly.
          Audit data has a 30-day TTL, so windows beyond that return partial results.
        </p>
      </div>

      {/* Date range controls */}
      <Card>
        <CardBody className="flex flex-wrap items-end gap-3">
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={preset === "7d" ? "primary" : "secondary"}
              onClick={() => setPresetRange("7d")}
            >
              Last 7 days
            </Button>
            <Button
              size="sm"
              variant={preset === "30d" ? "primary" : "secondary"}
              onClick={() => setPresetRange("30d")}
            >
              Last 30 days
            </Button>
            <Button
              size="sm"
              variant={preset === "custom" ? "primary" : "secondary"}
              onClick={() => setPreset("custom")}
            >
              Custom
            </Button>
          </div>
          {preset === "custom" && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-xs text-gray-500">From</div>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="rounded-md border border-gray-200 bg-white px-2 py-1 text-sm"
              />
              <div className="text-xs text-gray-500">to</div>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="rounded-md border border-gray-200 bg-white px-2 py-1 text-sm"
              />
            </div>
          )}
          <div className="ml-auto text-xs text-gray-500">
            {loading ? "Loading…" : data ? `${formatDate(data.range.from)} → ${formatDate(data.range.to)}` : "—"}
          </div>
        </CardBody>
      </Card>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          icon={<ArrowDownRight className="h-4 w-4" />}
          label="Input tokens"
          value={data ? formatNumber(data.total.inputTokens) : "—"}
        />
        <SummaryCard
          icon={<ArrowUpRight className="h-4 w-4" />}
          label="Output tokens"
          value={data ? formatNumber(data.total.outputTokens) : "—"}
        />
        <SummaryCard
          icon={<Coins className="h-4 w-4" />}
          label="Estimated cost"
          value={data ? formatCost(data.total.cost) : "—"}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* By tenant */}
        <Card>
          <CardHeader title="By tenant" description={data ? `${data.byTenant.length} tenants used Bedrock in this window` : ""} />
          <CardBody>
            {!data || data.byTenant.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">No tenants used Bedrock in this window.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    <th className="py-2">Tenant</th>
                    <th className="py-2 text-right">Input</th>
                    <th className="py-2 text-right">Output</th>
                    <th className="py-2 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byTenant.map((t) => (
                    <tr key={t.tenantId} className="border-b border-gray-50 last:border-0">
                      <td className="py-2">
                        <div className="font-medium text-gray-900">{t.displayName}</div>
                        <div className="text-xs text-gray-500 font-mono">{t.tenantId}</div>
                      </td>
                      <td className="py-2 text-right font-mono text-xs text-gray-700">{formatNumber(t.inputTokens)}</td>
                      <td className="py-2 text-right font-mono text-xs text-gray-700">{formatNumber(t.outputTokens)}</td>
                      <td className="py-2 text-right font-mono text-xs font-semibold text-gray-900">{formatCost(t.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>

        {/* By model */}
        <Card>
          <CardHeader title="By model" description={data ? `${data.byModel.length} distinct models` : ""} />
          <CardBody>
            {!data || data.byModel.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">No usage in this window.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    <th className="py-2">Model</th>
                    <th className="py-2 text-right">Input</th>
                    <th className="py-2 text-right">Output</th>
                    <th className="py-2 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byModel.map((m) => (
                    <tr key={m.modelId} className="border-b border-gray-50 last:border-0">
                      <td className="py-2">
                        <div className="font-medium text-gray-900">{m.displayName}</div>
                        <div className="text-[10px] text-gray-400 font-mono">{m.modelId}</div>
                      </td>
                      <td className="py-2 text-right font-mono text-xs text-gray-700">{formatNumber(m.inputTokens)}</td>
                      <td className="py-2 text-right font-mono text-xs text-gray-700">{formatNumber(m.outputTokens)}</td>
                      <td className="py-2 text-right font-mono text-xs font-semibold text-gray-900">
                        {m.cost == null ? <span className="text-gray-400">—</span> : formatCost(m.cost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      </div>

      {/* By day */}
      <Card>
        <CardHeader title="By day" description="Token volume per UTC day" />
        <CardBody>
          {!data || data.byDay.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">No daily usage in this window.</div>
          ) : (
            <div className="space-y-2">
              {data.byDay.map((d) => {
                const max = Math.max(...data.byDay.map((x) => x.inputTokens + x.outputTokens));
                const total = d.inputTokens + d.outputTokens;
                const pct = max > 0 ? (total / max) * 100 : 0;
                return (
                  <div key={d.day} className="flex items-center gap-3 text-xs">
                    <Calendar className="h-3 w-3 text-gray-400" />
                    <span className="w-24 font-mono text-gray-700">{d.day}</span>
                    <div className="flex-1">
                      <div className="h-2 rounded-full bg-gray-100">
                        <div
                          className="h-2 rounded-full bg-brand-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <span className="w-24 text-right font-mono text-gray-700">{formatNumber(total)} tok</span>
                    <span className="w-12 text-right text-gray-400">{d.eventCount} ev</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

void BarChart3;

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
          {icon}
          {label}
        </div>
        <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
      </CardBody>
    </Card>
  );
}

function formatNumber(n: number): string {
  if (n === 0) return "0";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}K`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function formatCost(n: number): string {
  if (n === 0) return "$0.00";
  if (n < 0.01) return "<$0.01";
  if (n < 100) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(0)}`;
}

function formatDate(iso: string): string {
  return iso.slice(0, 10);
}
